/**
 * Detail collapse for a sport club.
 */
import * as React from 'react';
import { Trophy, MapPin, Globe, Mail } from 'lucide-react';
import { LabelBadge } from '@/components/common/LabelBadge';
import { DetailsCard } from '@/components/common/DetailsCard';
import { DetailField } from '@/components/common/details/DetailField';
import { SPORT_CLUB_TYPE_CONFIG } from '@/components/sport-clubs/sport-club-configs';
import { deriveSportClubDetailDisplay } from '@/components/sport-clubs/sport-club-table-utils';
import { useDepartments } from '@/hooks/useDepartments';
import type { SportClub } from '@/types/sport-club.types';

/** Detail body: maps stored department code via the departments catalogue (`name` already includes the code). */
function SportClubDetails({ club }: Readonly<{ club: SportClub }>) {
  const clubTypeConfig = SPORT_CLUB_TYPE_CONFIG;
  const {
    typeConfig: sportConfig,
    websiteLabel,
    cityLine,
    createdAtLabel,
    updatedAtLabel,
  } = deriveSportClubDetailDisplay(club);
  const { data: departments = [] } = useDepartments('FR');
  const departmentLabel = club.department
    ? (departments.find((d) => d.code === club.department)?.name ?? club.department)
    : '';

  return (
    <>
      <div className="details-view-header">
        <div className="details-view-header-left">
          <div className="details-view-header-icon">
            <Trophy className="w-6 h-6 text-[var(--color-3-nuance-1)]" />
          </div>
          <div className="details-view-header-content">
            <div className="details-view-header-with-badges">
              <h3 className="details-view-header-title">{club.name}</h3>
              <LabelBadge
                label={clubTypeConfig.label}
                icon={clubTypeConfig.icon}
                variant={clubTypeConfig.badgeVariant}
                color={clubTypeConfig.badgeColor}
                size="sm"
              />
              {club.sportType && (
                <LabelBadge
                  label={sportConfig.label}
                  icon={sportConfig.icon}
                  variant={sportConfig.badgeVariant}
                  color={sportConfig.badgeColor}
                  size="sm"
                />
              )}
            </div>
            {club.description && <p className="details-view-header-subtitle">{club.description}</p>}
          </div>
        </div>
      </div>

      <div className="details-view-grid mt-6">
        <DetailsCard icon={Trophy} title="Sport" colorName="color-3" colSpan={1}>
          <div className="space-y-3">
            <DetailField
              label="Sport"
              value={
                club.sportType ? (
                  <LabelBadge
                    label={sportConfig.label}
                    icon={sportConfig.icon}
                    variant={sportConfig.badgeVariant}
                    color={sportConfig.badgeColor}
                    size="sm"
                  />
                ) : undefined
              }
            />
            <DetailField label="Description" value={club.description} />
          </div>
        </DetailsCard>

        <DetailsCard icon={MapPin} title="Coordonnées" colorName="color-2" colSpan={1}>
          <div className="space-y-3">
            <DetailField label="Adresse" value={club.address} />
            <DetailField label="Ville" value={cityLine} />
            {departmentLabel && <DetailField label="Département" value={departmentLabel} />}
            <DetailField label="Pays" value={club.country} />
          </div>
        </DetailsCard>

        <DetailsCard icon={Mail} title="Contact" colorName="color-1" colSpan={2}>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-3">
              {club.contactEmail ? (
                <DetailField
                  label="Email"
                  value={
                    <a
                      href={`mailto:${club.contactEmail}`}
                      className="text-[var(--color-3-nuance-1)] hover:underline text-sm"
                    >
                      {club.contactEmail}
                    </a>
                  }
                />
              ) : (
                <DetailField label="Email" value={undefined} />
              )}
            </div>
            <div className="space-y-3">
              {club.website ? (
                <DetailField
                  label="Site web"
                  value={
                    <a
                      href={club.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--color-3-nuance-1)] hover:underline text-sm flex items-center gap-1"
                    >
                      <Globe className="w-3 h-3" />
                      {websiteLabel}
                    </a>
                  }
                />
              ) : (
                <DetailField label="Site web" value={undefined} />
              )}
            </div>
          </div>
          <div className="mt-3 pt-3 border-t space-y-3">
            <DetailField label="Date de création" value={createdAtLabel} />
            <DetailField label="Dernière mise à jour" value={updatedAtLabel} />
          </div>
        </DetailsCard>
      </div>
    </>
  );
}

/** Details renderer passed to the sport-clubs {@link DataTable}. */
export const renderSportClubDetails = (club: SportClub): React.ReactNode => {
  return <SportClubDetails club={club} />;
};
